import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context.ts';
import {
  tFetchTemplatesResponse,
  tPredefinedVariableDefinition,
  tPredefinedVariables,
  tTemplates,
} from 'src/pages/Pipe/Pipe.types.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context.ts';
import { eWhatsappApiWindow } from 'src/pages/WhatsappApi/WhatsappApi.enum.ts';
import useOpenUtilityTemplateEditor from 'src/pages/WhatsappApi/hooks/useOpenUtilityTemplateEditor.hook';
import { RouterPaths } from 'src/components/Router/Router.constants.ts';
import { PipeService } from 'src/pages/Pipe/Pipe.service.ts';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { ChatContext } from 'src/components/Chat/Chat.constants.ts';

/**
 * Custom hook to manage WhatsApp Business API (WAB) template actions in the chat interface.
 * Handles template fetching, filtering, searching, and navigation to template management.
 *
 * @returns {Object} Hook state and methods
 * @returns {boolean} loading - Indicates if templates are being loaded
 * @returns {tTemplates[]} filteredTemplates - Array of templates filtered by search and type
 * @returns {('ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY')} selectedTemplateType - Current selected template type filter
 * @returns {Function} handleNavigateToTemplates - Navigate to the templates view in WhatsApp API settings
 * @returns {Function} handleSearch - Handle search input to filter templates by name, ID, or content
 * @returns {Function} handleTemplateTypeFilter - Filter templates by type (ALL, FOLLOWING, MARKETING, UTILITY)
 */
export function useWabTemplatesAction() {
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { setWhatsappApiWindow } = useWhatsappApiContext();
  // INT-1500 U2: opens the template editor with UTILITY (Seguimiento) already
  // chosen, from the picker's empty state.
  const handleCreateUtilityTemplate = useOpenUtilityTemplateEditor();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const { setChatModalIsOpen, selectedLeadForChatModal } = useModalsContext();
  const [template, setTemplate] = useState<tTemplates[]>([]);
  const [filteredTemplates, setFilteredTemplates] =
    useState<tTemplates[]>(template);
  const [predefinedVariables, setPredefinedVariables] = useState<tPredefinedVariables | undefined>();
  const [variableDefinitions, setVariableDefinitions] = useState<tPredefinedVariableDefinition[]>([]);
  // The context itself, not useChatContext from Chat.hook: that module pulls
  // the whole chat graph into this picker, which also mounts outside the chat
  // provider (see the BUG-915 note below).
  const { templatePickerRequest, setTemplatePickerRequest } =
    useContext(ChatContext);
  const [selectedTemplateType, setSelectedTemplateType] = useState<
    'ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY'
  >(templatePickerRequest?.templateType ?? 'ALL');
  // The load below resolves after the filter may have changed, so it reads the
  // filter from here rather than from the state its closure captured.
  const selectedTemplateTypeRef = useRef(selectedTemplateType);
  selectedTemplateTypeRef.current = selectedTemplateType;

  /*
    BUG-915 — this call must NOT go through `usePipeContext()`.

    `PipeProvider` is mounted in exactly one place: `Pipe.page.tsx`, the
    `/workspace/funnel` route. The chat is reachable from two other places that
    sit outside it — `/workspace/chat` (`MessengerV2/Messenger.page.tsx`) and the
    Directory `ChatModal`. There `usePipeContext()` does not throw; it silently
    resolves to `Pipe.constants.ts` `initialState`, whose `getTemplates` returns
    `{ templates: [] }` without issuing a request at all. The picker then renders
    an empty list with no error and nothing in the console.

    The picker needs no Pipe state — the Pipe version of this function was itself
    only a `PipeService` call plus an error toast — so it calls the service
    directly and works from every chat entry point.

    Do NOT "fix" a future variant of this by reaching for `usePipe()` either:
    that mounts a second, independent copy of the Pipe hook (its own state and
    its own socket subscriptions), which is exactly what BUG-807 / PR #3197
    removed. Guarded by `WabTemplatesAction.outsideProvider.test.tsx`.
  */
  async function getTemplates(workspaceId: string, handle?: string) {
    try {
      const { templates, predefined_variables }: tFetchTemplatesResponse =
        await PipeService.getTemplates(workspaceId, handle);

      return {
        templates: templates as tTemplates[],
        predefinedVariables: predefined_variables,
      };
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: 'Error al obtener plantillas: ' + error,
      });
    }
  }

  /*
    Latest-wins guard. The effect below re-runs when the recipient handle
    changes, and `Chat.hook.ts` documents that the handle can arrive as a LATE
    write (BUG-792), so two loads genuinely overlap in practice. The catalog is
    recipient-scoped — `PipeService.getTemplates` sends `handle` — so an older
    run resolving last would leave the WRONG recipient's templates on screen.

    This is reachable only now: before the BUG-915 fix the context stub never
    issued a request on `/workspace/chat` or the Directory `ChatModal`, so
    there was nothing to race. Making the request real is what arms it.
  */
  const requestIdRef = useRef(0);

  /**
   * Fetches and displays WhatsApp templates for the current workspace.
   * Sets loading state during the fetch operation and updates both template and filteredTemplates states.
   * If no templates are found, sets empty arrays.
   */
  async function showTemplates() {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const [templatesRes, varsRes] = await Promise.all([
        getTemplates(currentWorkspaceId ?? '', selectedLeadForChatModal?.handle),
        PipeService.getPredefinedVariables().catch((err) => {
          LLog.warn(
            { component: LOG_COMPONENTS.UI, method: 'showTemplates', operation: LOG_OPERATIONS.ERROR },
            'Failed to fetch predefined variables',
            { error: String(err) },
          );
          return { variables: [] };
        }),
      ]);

      // A newer load started while this one was in flight — discard this result
      // rather than clobbering the newer recipient's catalog.
      if (requestId !== requestIdRef.current) return;

      if (!templatesRes) {
        /*
          The fetch failed and `getTemplates` has already raised the toast.
          Reset every slice the success branch below owns: clearing `template`
          alone left `filteredTemplates` — what the picker actually renders —
          showing the PREVIOUS recipient's catalog while `loading` read false,
          which is the one state no consumer can render coherently.
          `varsRes` has its own `.catch`, so it is always safe to read here.
        */
        setTemplate([]);
        setFilteredTemplates([]);
        setPredefinedVariables(undefined);
        setVariableDefinitions(varsRes.variables);
        return;
      }

      setTemplate(templatesRes.templates);
      applyTemplateTypeFilter(
        templatesRes.templates,
        selectedTemplateTypeRef.current,
      );
      setPredefinedVariables(templatesRes.predefinedVariables);
      setVariableDefinitions(varsRes.variables);
    } finally {
      // Only the newest run owns the spinner; a superseded run clearing it
      // would show an empty picker as "done" while the real load is pending.
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  /**
   * Navigates to the WhatsApp API templates management page.
   * Sets the WhatsApp API window to VIEW_TEMPLATES, closes the chat modal, and navigates to the WAPI route.
   */
  function handleNavigateToTemplates() {
    setWhatsappApiWindow(eWhatsappApiWindow.VIEW_TEMPLATES);
    setChatModalIsOpen(false);
    navigate(RouterPaths.wapi);
  }


  /**
   * Handles search input changes to filter templates by name, template ID, or component content.
   * Applies both search and template type filters to the template list.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event containing the search term
   */
  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const searchTerm = event.target.value.toLowerCase();
    const filtered = template.filter(
      (template) =>
        template.name.toLowerCase().includes(searchTerm) ||
        template.template_id.toLowerCase().includes(searchTerm) ||
        template.components.toLowerCase().includes(searchTerm),
    );
    applyTemplateTypeFilter(filtered, selectedTemplateType);
  }

  /**
   * Filters templates by the selected template type category.
   * Updates the selected template type state and applies the filter to current templates.
   *
   * @param {('ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY')} type - The template type to filter by
   */
  function handleTemplateTypeFilter(
    type: 'ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY',
  ) {
    setSelectedTemplateType(type);
    applyTemplateTypeFilter(template, type);
  }

  /**
   * Applies template type filtering logic to a given array of templates.
   * Filters templates based on the specified type: ALL shows all templates,
   * FOLLOWING shows both FOLLOWING and MARKETING types, UTILITY shows only UTILITY type.
   * Updates the filteredTemplates state with the filtered results.
   *
   * @param {tTemplates[]} templates - The array of templates to filter
   * @param {('ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY')} type - The template type to filter by
   */
  function applyTemplateTypeFilter(
    templates: tTemplates[],
    type: 'ALL' | 'FOLLOWING' | 'MARKETING' | 'UTILITY',
  ) {
    if (type === 'ALL') {
      setFilteredTemplates(templates);
    } else if (type !== 'UTILITY') {
      const filtered = templates.filter(
        (template) =>
          template.template_type === 'FOLLOWING' ||
          template.template_type === 'MARKETING',
      );
      setFilteredTemplates(filtered);
    } else {
      const filtered = templates.filter(
        (template) => template.template_type === type,
      );
      setFilteredTemplates(filtered);
    }
  }

  useEffect(() => {
    showTemplates();
  }, [currentWorkspaceId, selectedLeadForChatModal?.handle]);

  // INT-1504: the Meta error notice opened the picker (or asked again while it
  // was open) with a filter, e.g. Seguimiento (Meta's UTILITY) for 131049. Apply it and consume the
  // request so the next plain open starts from "Todas".
  useEffect(() => {
    if (!templatePickerRequest) return;
    handleTemplateTypeFilter(templatePickerRequest.templateType);
    setTemplatePickerRequest(null);
  }, [templatePickerRequest]);

  return {
    loading,
    filteredTemplates,
    predefinedVariables,
    variableDefinitions,
    selectedTemplateType,
    // INT-1500 U2: from the whole catalog, not the filtered list, so a search
    // with no match never reads as "no follow-up templates".
    hasUtilityTemplates: template.some(
      (candidate) => candidate.template_type === 'UTILITY',
    ),
    handleNavigateToTemplates,
    handleCreateUtilityTemplate,
    handleSearch,
    handleTemplateTypeFilter,
  };
}
